import { useEffect, useRef, useState } from 'react';

/**
 * Counts from 0 to `end` once the element scrolls into view.
 * Returns a ref to attach to the display element and the current value.
 */
export function useCountUp(end: number, duration = 1800) {
    const [value, setValue] = useState(0);
    const ref = useRef<HTMLSpanElement>(null);
    const started = useRef(false);

    useEffect(() => {
        const node = ref.current;
        if (!node) return;

        const prefersReduced =
            typeof window !== 'undefined' &&
            window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

        if (prefersReduced) {
            setValue(end);
            return;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && !started.current) {
                    started.current = true;
                    const start = performance.now();
                    const tick = (now: number) => {
                        const progress = Math.min((now - start) / duration, 1);
                        // easeOutExpo
                        const eased = progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress);
                        setValue(Math.round(eased * end));
                        if (progress < 1) requestAnimationFrame(tick);
                    };
                    requestAnimationFrame(tick);
                }
            },
            { threshold: 0.4 }
        );

        observer.observe(node);
        return () => observer.disconnect();
    }, [end, duration]);

    return { ref, value };
}
